/**
 * Tailwind CSS implementation of SegmentedControl component
 * Uses Seeds segmented-control CSS classes from segmented-control.css
 */

import * as React from "react";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { ToggleButton } from "./styles";
import type {
  TypeSegmentedControlProps,
  TypeSegmentedControlItemProps,
} from "./SegmentedControlTypes";

/**
 * Identifies which rendering path the surrounding SegmentedControl took so the
 * compound `.Item` can match it. The styled-components path renders the legacy
 * styled `ToggleButton` (styled at runtime, no CSS-file dependency); the
 * Tailwind path renders the pure CSS-class button. The Hybrid Root provides
 * "styled" on its styled branch; the default is "tailwind".
 */
type SegmentedControlVariant = "tailwind" | "styled";
export const SegmentedControlVariantContext =
  React.createContext<SegmentedControlVariant>("tailwind");

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

/**
 * Builds the Radix `onValueChange` handler shared by both the Tailwind and the
 * styled-components paths. Preserves the deselect guard and the synthetic
 * `change` event used for `onChange` backwards compatibility.
 */
export function createChangeHandler(
  onChange?: (e: React.SyntheticEvent<HTMLInputElement>) => void,
  onValueChange?: (value: string) => void
) {
  return (e: string) => {
    // Radix allows deselecting a value by clicking on it when it's already selected,
    // but we don't want to allow that behavior in our SegmentedControl.
    if (!e) return;

    onValueChange?.(e);
    // Create a mock event to pass to onChange for backwards compatibility.
    // We want to move towards onValueChange, but onChange is used by consumers
    // currently so we need to support both for now.
    const mockEvent = new Event("change", {
      bubbles: true,
    }) as unknown as React.SyntheticEvent<HTMLInputElement>;
    Object.defineProperty(mockEvent, "target", { value: { value: e } });
    Object.defineProperty(mockEvent, "currentTarget", {
      value: { value: e },
    });
    onChange?.(mockEvent);
  };
}

/**
 * Compound item routed to match the surrounding container's path.
 *
 * On the styled-components path it renders the legacy styled `ToggleButton`
 * (styled at runtime by styled-components, with no dependency on the component
 * CSS file) — this preserves backward compatibility for consumers routed to
 * the styled branch via a Box system prop or a `styled(SegmentedControl)`
 * extension. On the Tailwind path it renders the pure CSS-class button. The
 * host element is typed as `any` so Radix's `asChild`-injected props
 * (data-state, role, keyboard handlers, onClick) and any forwarded props
 * spread cleanly — mirrors ButtonTailwind's `Component as any` pattern.
 */
export const SegmentedControlItem = ({
  value,
  children,
  disabled,
  ...rest
}: TypeSegmentedControlItemProps) => {
  const variant = React.useContext(SegmentedControlVariantContext);

  if (variant === "styled") {
    // `Button` sets its own `type`, so we do not pass `type` here.
    return (
      <ToggleGroup.Item value={value} disabled={disabled} asChild>
        <ToggleButton
          data-qa-segmentedcontrol-item={value}
          disabled={disabled}
          {...rest}
        >
          {children}
        </ToggleButton>
      </ToggleGroup.Item>
    );
  }

  const Toggle = "button" as any;

  return (
    <ToggleGroup.Item value={value} disabled={disabled} asChild>
      <Toggle
        type="button"
        data-qa-segmentedcontrol-item={value}
        disabled={disabled}
        {...rest}
        className="seeds-segmented-control-item"
      >
        {children}
      </Toggle>
    </ToggleGroup.Item>
  );
};

SegmentedControlItem.displayName = "SegmentedControl.Item";

export const SegmentedControlTailwind = React.forwardRef<
  HTMLDivElement,
  TypeSegmentedControlProps
>(
  (
    {
      selectedValue,
      label,
      onChange,
      onValueChange,
      children,
      disabled = false,
      className,
      ...rest
    }: TypeSegmentedControlProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    // Plain <div> container, typed as `any` so the `asChild`-merged className
    // and any forwarded props spread cleanly — mirrors ButtonTailwind.
    const Container = "div" as any;

    return (
      <ToggleGroup.Root
        type="single"
        value={selectedValue}
        aria-label={label}
        disabled={disabled}
        onValueChange={createChangeHandler(onChange, onValueChange)}
        asChild
      >
        <Container
          ref={ref}
          data-qa-segmentedcontrol={label}
          data-qa-segmentedcontrol-value={selectedValue}
          {...rest}
          // className is set last (after {...rest}) so the consumer-provided
          // className is merged rather than overwritten.
          className={cn(
            "seeds-segmented-control",
            { "seeds-segmented-control-disabled": disabled },
            className
          )}
        >
          {children}
        </Container>
      </ToggleGroup.Root>
    );
  }
);

SegmentedControlTailwind.displayName = "SegmentedControlTailwind";
